/**
 * AI Assistant Drawer — DeepSeek-powered Form Creator
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A conversational chat drawer that slides in from the right of the
 * FieldsManager canvas. Users describe the form they want; DeepSeek replies
 * with a message + an optional schema. When a schema arrives, the user can
 * click "Apply to form" to replace the current builder schema.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Sparkles, Send, X, Check,
  RotateCcw, User, Wand2,
} from 'lucide-react';
import { aiAPI } from '../../services/api';
import { getLocalized } from '../../i18n/getLocalized';

// Friendly welcome suggestions — resolved through i18n so the starter prompts
// are written in the user's language (they are sent to the model verbatim).
const SUGGESTION_KEYS = [
  'fieldsManager.aiAssistant.suggestionNda',
  'fieldsManager.aiAssistant.suggestionVendor',
  'fieldsManager.aiAssistant.suggestionEmployment',
  'fieldsManager.aiAssistant.suggestionLease',
];

function SchemaPreviewCard({ schema, onApply, applied, t, lang }) {
  const sectionCount = schema?.sections?.length || 0;
  const fieldCount = (schema?.sections || []).reduce(
    (acc, s) => acc + (s.type === 'table' ? (s.tableColumns?.length || 0) : (s.fields?.length || 0)),
    0,
  );

  return (
    <div className={`fm-ai-schema-card ${applied ? 'is-applied' : ''}`}>
      <div className="fm-ai-schema-card__head">
        <span className="fm-ai-schema-card__icon"><Wand2 /></span>
        <div className="fm-ai-schema-card__titles">
          <strong>{t('fieldsManager.aiAssistant.generatedSchema')}</strong>
          <span>
            {t('fieldsManager.topBar.sectionsCount', { count: sectionCount })}
            {' · '}
            {t('fieldsManager.topBar.fieldsCount', { count: fieldCount })}
          </span>
        </div>
      </div>
      <ul className="fm-ai-schema-card__sections">
        {(schema?.sections || []).map((s, i) => (
          <li key={i} style={{ '--section-dot': s.color }}>
            <span className="fm-ai-schema-card__dot" />
            <span className="fm-ai-schema-card__title">{getLocalized(s.title, lang)}</span>
            <span className="fm-ai-schema-card__meta">
              {s.type === 'table'
                ? t('fieldsManager.sectionCard.colsCount', { count: s.tableColumns?.length || 0 })
                : t('fieldsManager.sectionCard.fieldsCount', { count: s.fields?.length || 0 })}
            </span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className={`fm-ai-schema-card__apply ${applied ? 'is-applied' : ''}`}
        onClick={onApply}
        disabled={applied}
      >
        {applied ? (<><Check /> {t('fieldsManager.aiAssistant.appliedToForm')}</>) : (<><Wand2 /> {t('fieldsManager.aiAssistant.applyToForm')}</>)}
      </button>
    </div>
  );
}

export default function AIAssistant({
  open,
  onClose,
  onApplySchema,
  currentSchema,
  docked = false,
  collapsed = false,
  onToggleCollapse,
  initialMessages = null,
}) {
  const { t } = useTranslation();
  const { branding } = useSelector((state) => state.settings);
  const { currentLanguage } = useSelector((state) => state.language);
  const logoUrl = branding?.logoUrl || '';
  const companyName = getLocalized(branding?.companyName, currentLanguage);

  // Seed messages from showoff handoff (only used on first mount)
  const [messages, setMessages] = useState(() => initialMessages || []);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to newest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when opening (drawer mode) or on mount (docked mode)
  useEffect(() => {
    if (docked || open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open, docked]);

  const send = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || loading) return;

    // Build wire history (only role + content) BEFORE appending the new user msg
    const wireHistory = messages.map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);

    try {
      const res = await aiAPI.generateSchema(trimmed, wireHistory, currentSchema || null);
      const { reply, schema } = res.data || {};
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: reply || t('fieldsManager.aiAssistant.done'),
          schema: schema || null,
          applied: false,
        },
      ]);
    } catch (err) {
      const errMsg = err?.response?.data?.message
        || err?.response?.data?.error
        || t('fieldsManager.aiAssistant.error');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `⚠️ ${errMsg}`, schema: null, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  }, [loading, messages, currentSchema, t]);

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const handleApply = (msgIdx, schema) => {
    onApplySchema(schema);
    setMessages((prev) => prev.map((m, i) => (i === msgIdx ? { ...m, applied: true } : m)));
  };

  const reset = () => {
    setMessages([]);
    setInput('');
  };

  if (!docked && !open) return null;

  // ── Docked (always-open) variant ─────────────────────────────────────────
  if (docked) {
    return (
      <aside
        className={`fmx-dock ${collapsed ? 'fmx-dock--collapsed' : ''}`}
        role="complementary"
        aria-label={t('fieldsManager.aiAssistant.panelLabel')}
      >
        {/* Collapsed: vertical rail */}
        {collapsed && (
          <button
            type="button"
            className="fmx-dock__rail"
            onClick={onToggleCollapse}
            title={t('fieldsManager.aiAssistant.expand')}
          >
            <span className="fmx-dock__rail-icon">
              <Sparkles />
              <span className="fmx-dock__rail-pulse" aria-hidden="true" />
            </span>
            <span className="fmx-dock__rail-label">AI</span>
          </button>
        )}

        {/* Expanded: full panel */}
        {!collapsed && (
          <>
            <header className="fmx-dock__head">
              <div className="fmx-dock__identity">
                <span className="fmx-dock__avatar">
                  {logoUrl
                    ? <img src={logoUrl} alt={companyName} />
                    : <Sparkles />}
                  <span className="fmx-dock__online" aria-hidden="true" />
                </span>
                <div className="fmx-dock__title-block">
                  <div className="fmx-dock__title">Sentry</div>
                  <div className="fmx-dock__sub">
                    {loading
                      ? t('fieldsManager.aiAssistant.thinking')
                      : t('fieldsManager.aiAssistant.statusIdle')}
                  </div>
                </div>
              </div>

              <div className="fmx-dock__head-actions">
                {messages.length > 0 && (
                  <button
                    type="button"
                    className="fmx-iconbtn fmx-iconbtn--ghost"
                    onClick={reset}
                    title={t('fieldsManager.aiAssistant.clearConversation')}
                  >
                    <RotateCcw />
                  </button>
                )}
                {onToggleCollapse && (
                  <button
                    type="button"
                    className="fmx-iconbtn fmx-iconbtn--ghost"
                    onClick={onToggleCollapse}
                    title={t('fieldsManager.aiAssistant.collapse')}
                  >
                    <X />
                  </button>
                )}
              </div>
              {loading && <div className="fmx-dock__progress" aria-hidden="true" />}
            </header>

            <div className="fmx-dock__body" ref={scrollRef}>
              {messages.length === 0 && (
                <div className="fmx-dock__welcome">
                  <div className="fmx-dock__welcome-icon">
                    <Sparkles />
                  </div>
                  <h3 className="fmx-dock__welcome-title">
                    {t('fieldsManager.aiAssistant.greeting')}
                  </h3>
                  <p className="fmx-dock__welcome-desc">
                    {t('fieldsManager.aiAssistant.welcomeDescription')}
                  </p>
                  <div className="fmx-dock__suggestions">
                    {SUGGESTION_KEYS.map((key) => {
                      const s = t(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          className="fmx-dock__suggestion"
                          onClick={() => send(s)}
                        >
                          <Wand2 />
                          <span>{s}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`fmx-msg fmx-msg--${m.role === 'user' ? 'user' : 'ai'}`}
                >
                  {m.role !== 'user' && (
                    <div className="fmx-msg__avatar">
                      <Sparkles />
                    </div>
                  )}
                  <div className={`fmx-msg__bubble ${m.error ? 'is-error' : ''}`}>
                    <div className="fmx-msg__text">{m.content}</div>
                    {m.schema && (
                      <SchemaPreviewCard
                        schema={m.schema}
                        onApply={() => handleApply(i, m.schema)}
                        applied={!!m.applied}
                        t={t}
                        lang={currentLanguage}
                      />
                    )}
                  </div>
                  {m.role === 'user' && (
                    <div className="fmx-msg__avatar fmx-msg__avatar--user">
                      <User />
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="fmx-msg fmx-msg--ai">
                  <div className="fmx-msg__avatar"><Sparkles /></div>
                  <div className="fmx-msg__bubble fmx-msg__bubble--loading">
                    <span className="fmx-msg__dot" />
                    <span className="fmx-msg__dot" />
                    <span className="fmx-msg__dot" />
                  </div>
                </div>
              )}
            </div>

            <form className="fmx-dock__composer" onSubmit={onSubmit}>
              <div className="fmx-dock__composer-inner">
                <textarea
                  ref={inputRef}
                  className="fmx-dock__input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={messages.length === 0
                    ? t('fieldsManager.aiAssistant.inputPlaceholder')
                    : t('fieldsManager.aiAssistant.followUpPlaceholder')}
                  rows={1}
                  disabled={loading}
                />
                <button
                  type="submit"
                  className="fmx-dock__send"
                  disabled={!input.trim() || loading}
                  title={t('fieldsManager.aiAssistant.send')}
                >
                  <Send />
                </button>
              </div>
              <div className="fmx-dock__hint">
                <kbd>Enter</kbd> {t('fieldsManager.aiAssistant.hintSend')} · <kbd>Shift</kbd>+<kbd>Enter</kbd> {t('fieldsManager.aiAssistant.hintNewline')}
              </div>
            </form>
          </>
        )}
      </aside>
    );
  }

  // Non-docked variant is no longer used (FieldsManager renders <AIAssistant docked />).
  return null;
}
